/**
 * Types for the core video feature (Chime SDK integration layer).
 * Business-logic types live in videoMeeting.
 */

// --- UI types ---

export type VideoUIActiveTab = "chat" | "roster" | "materials";

export type ContentShareZoom = {
  scale: number;
  translateX: number;
  translateY: number;
  isZoomed: boolean;
};

export type ScrollPositions = {
  [K in VideoUIActiveTab]: number;
};

// --- Config enums ---

export enum MeetingMode {
  Spectator,
  Attendee,
}

export enum Layout {
  Gallery,
  Featured,
}

export const VideoFiltersCpuUtilization = {
  Disabled: "0",
  CPU10Percent: "10",
  CPU20Percent: "20",
  CPU40Percent: "40",
} as const;

export const VideoTransformOptions = {
  None: "None",
  Blur: "Background Blur",
  Replacement: "Background Replacement",
} as const;

// --- Transcription types ---

export type TranscriptionSegment = {
  id: string;
  speakerId: string;
  speakerName: string;
  text: string;
  confidence: number;
  isPartial: boolean;
  startTime: number;
  endTime: number;
};

// --- Tile Grid types ---

/** Per-attendee visual state computed by useChimeObserver's state machine. */
export type TileState = 'video' | 'frozen' | 'avatar' | 'absent';

/** @deprecated Use TileState instead. Kept during migration. */
export type AttendeeVideoState = {
  attendeeId: string;
  name: string;
  videoEnabled: boolean;
  bandwidthConstrained: boolean;
};

export type VideoSourceState = {
  cameraSources: string[];
  activeSpeakersWithCameraSource: string[];
  contentShareId: string | null;
  hasLocalVideo: boolean;
  hasLocalContentSharing: boolean;
};

// --- Device intent (data channel) ---

export type AttendeeDeviceIntent = {
  cameraIntentOn: boolean;
  micIntentOn: boolean;
  lastUpdate: number;
};

// --- Connection quality ---

export type ConnectionQuality = 'good' | 'poor' | 'suggestStopVideo';

// --- Events ---

export type VideoEvents = {
  "videochat:joined": { meetingId: string };
  "videochat:left": { meetingId: string };
  "videochat:muted": Record<string, never>;
  "videochat:unmuted": Record<string, never>;
  "videochat:camera:on": Record<string, never>;
  "videochat:camera:off": Record<string, never>;
  "videochat:audio:source:selected": { deviceId: string };
  "videochat:video:source:selected": { deviceId: string };
  "videochat:screen:shared": Record<string, never>;
  "videochat:screen:unshared": Record<string, never>;
  "videochat:participant:viewed": Record<string, never>;
  "videochat:materials:opened": Record<string, never>;
  "transcription:started": Record<string, never>;
  "materials:article:viewed": { articleId: string };
  "materials:video:play": { videoId: string };
  "materials:video:pause": { videoId: string };
  "materials:video:seek": { videoId: string };
  "action:opened": { stepId: string };
  "action:completed": { stepId: string };
  "videochat:active:speaker:started": { userId: string };
  "videochat:active:speaker:stopped": { userId: string };
  "videochat:error": { errorType: string };
};
